import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { DelKnapp } from "./DelKnapp";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Droplets, ExternalLink } from "lucide-react";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from "recharts";
import { endringsLinjer } from "@/components/endringMerke";
import { fmt, tooltipStyle } from "./oversiktConstants";
import { dec } from "@/lib/formatters";

const MAIN = [
  { key: "vann", label: "Vassforsyning", color: "#103766" },
  { key: "avlop", label: "Avløp", color: "#3F8A86" },
  { key: "avfall", label: "Avfall", color: "#E8A34A" },
  { key: "feiing", label: "Feiing og tilsyn", color: "#D35E4E" },
  { key: "sum_arsgebyr", label: "Sum årsgebyr", color: "#1F2A36" },
];
const EXTRA = [
  { key: "septik", label: "Septiktømming (årsgebyr)" },
  { key: "malerleie", label: "Målarleige" },
  { key: "vann_m3", label: "Vatn per m³" },
  { key: "avlop_m3", label: "Avløp per m³" },
  { key: "tilknytning_vann", label: "Tilknytningsgebyr vatn (éin sats)" },
  { key: "tilknytning_vann_lav", label: "Tilknytningsgebyr vatn (låg)" },
  { key: "tilknytning_vann_hoy", label: "Tilknytningsgebyr vatn (høg)" },
  { key: "tilknytning_avlop", label: "Tilknytningsgebyr avløp (éin sats)" },
  { key: "tilknytning_avlop_lav", label: "Tilknytningsgebyr avløp (låg)" },
  { key: "tilknytning_avlop_hoy", label: "Tilknytningsgebyr avløp (høg)" },
];

const GebyrSection = ({ data, averages, kommuneCode, kommuneNamn }) => {
  const rows = data || [];
  const latest = rows[rows.length - 1];
  const prev = rows[rows.length - 2];
  const fylkeLabel = averages?.fylkeName ? `Snitt ${averages.fylkeName}` : "Fylkessnitt";
  const avgByYear = (list, year) => list?.find((r) => r.year === year)?.sum_arsgebyr ?? null;
  const chartRows = rows.map((r) => ({
    ...r,
    fylke_sum: avgByYear(averages?.fylke, r.year),
    land_sum: avgByYear(averages?.land, r.year),
  }));
  const fylkeLatest = latest ? avgByYear(averages?.fylke, latest.year) : null;
  const landLatest = latest ? avgByYear(averages?.land, latest.year) : null;
  const diffPct = (v) => (v && latest ? ((latest.sum_arsgebyr - v) / v) * 100 : null);
  const change = latest && prev && prev.sum_arsgebyr ? ((latest.sum_arsgebyr - prev.sum_arsgebyr) / prev.sum_arsgebyr) * 100 : null;
  const allRows = [...MAIN, ...EXTRA].filter((f) => rows.some((r) => r[f.key] != null));

  return (
    <Card className="bg-white border border-[#E6E9F0] rounded-lg mb-8" data-testid="gebyr-section" id="gebyrer">
      <CardHeader className="border-b border-[#E6E9F0] pb-4">
        <div className="flex items-start justify-between gap-3">
          <CardTitle className="text-lg font-medium text-[#1F2A36] flex items-center gap-2">
            <Droplets className="w-5 h-5 text-[#103766]" />
            Kommunale gebyr for bustad
          </CardTitle>
          {kommuneCode && <DelKnapp kommuneCode={kommuneCode} kommuneNamn={kommuneNamn} tema="skatt" testId="del-skatt" />}
        </div>
        <p className="text-sm text-[#4B647F]">Årsgebyr for vatn, avløp, avfall og feiing – standardbustad, ekskl. mva (kr). Stipla linjer viser {averages?.fylke?.length ? "fylkes- og landssnitt" : "landssnittet"}.</p>
      </CardHeader>
      <CardContent className="pt-6">
        {rows.length === 0 ? (
          <div className="h-[160px] flex items-center justify-center text-[#8AA4BF]" data-testid="gebyr-empty">
            Ingen gebyrdata tilgjengeleg – klikk «Oppdater data»
          </div>
        ) : (
          <div className="grid grid-cols-1 xl:grid-cols-5 gap-6">
            <div className="xl:col-span-2 space-y-4">
              <div className="p-4 bg-[#F6F8FB] rounded-lg">
                <p className="text-xs font-semibold uppercase tracking-[0.1em] text-[#8AA4BF] mb-1">Sum årsgebyr {latest.year}</p>
                <p className="text-3xl font-light text-[#1F2A36]" data-testid="gebyr-sum-latest">{fmt(latest.sum_arsgebyr)} kr</p>
                {change != null && (
                  <p className={`text-sm mt-1 ${change > 0 ? "text-[#D35E4E]" : "text-[#3F8A86]"}`}>
                    {change > 0 ? "+" : ""}{dec(change, 1)} % frå {prev.year}
                  </p>
                )}
              </div>
              {(fylkeLatest || landLatest) && (
                <div className="grid grid-cols-2 gap-3" data-testid="gebyr-averages">
                  {[[fylkeLabel, fylkeLatest, "gebyr-fylke"], ["Landet", landLatest, "gebyr-land"]].map(([label, val, tid]) => (
                    <div key={tid} className="p-3 border border-[#E6E9F0] rounded-lg">
                      <p className="text-xs text-[#8AA4BF]">{label} {latest.year}</p>
                      <p className="text-lg font-light text-[#1F2A36]" data-testid={tid}>{val ? `${fmt(val)} kr` : "—"}</p>
                      {diffPct(val) != null && (
                        <p className={`text-xs ${diffPct(val) > 0 ? "text-[#D35E4E]" : "text-[#3F8A86]"}`}>
                          Kommunen ligger {dec(Math.abs(diffPct(val)), 1)} % {diffPct(val) > 0 ? "over" : "under"}
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              )}
              <div className="h-[260px]" data-testid="gebyr-chart">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={chartRows}>
                    {endringsLinjer(chartRows)}
                    <CartesianGrid strokeDasharray="3 3" stroke="#E6E9F0" />
                    <XAxis dataKey="year" stroke="#8AA4BF" fontSize={12} />
                    <YAxis stroke="#8AA4BF" fontSize={12} tickFormatter={(v) => `${(v / 1000).toFixed(0)}k`} />
                    <Tooltip formatter={(v) => `${fmt(v)} kr`} contentStyle={tooltipStyle} />
                    <Legend wrapperStyle={{ fontSize: 11 }} />
                    {MAIN.map((f) => (
                      <Line key={f.key} type="monotone" dataKey={f.key} name={f.label} stroke={f.color} strokeWidth={f.key === "sum_arsgebyr" ? 3 : 2} dot={{ fill: f.color, r: 3 }} connectNulls />
                    ))}
                    {averages?.fylke?.length > 0 && (
                      <Line type="monotone" dataKey="fylke_sum" name={`Sum – ${fylkeLabel}`} stroke="#8AA4BF" strokeWidth={2} strokeDasharray="5 5" dot={{ fill: "white", stroke: "#8AA4BF", r: 3 }} connectNulls />
                    )}
                    {averages?.land?.length > 0 && (
                      <Line type="monotone" dataKey="land_sum" name="Sum – Landet" stroke="#E8A34A" strokeWidth={2} strokeDasharray="5 5" dot={{ fill: "white", stroke: "#E8A34A", r: 3 }} connectNulls />
                    )}
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>
            <div className="xl:col-span-3 overflow-x-auto">
              <Table data-testid="gebyr-table">
                <TableHeader>
                  <TableRow className="bg-[#F6F8FB]">
                    <TableHead className="text-[#4B647F]">Gebyr (kr)</TableHead>
                    {rows.map((r) => <TableHead key={r.year} className="text-[#4B647F] text-right">{r.year}</TableHead>)}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {allRows.map((f) => (
                    <TableRow key={f.key} className={f.key === "sum_arsgebyr" ? "bg-[#F6F8FB] font-medium" : "hover:bg-[#F6F8FB]/50"}>
                      <TableCell className="text-sm">{f.label}</TableCell>
                      {rows.map((r) => <TableCell key={r.year} className="text-right text-sm">{fmt(r[f.key])}</TableCell>)}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </div>
        )}
        <p className="text-xs text-[#8AA4BF] mt-4 text-center">
          Kjelde:{" "}
          <a href="https://www.ssb.no/statbank/table/12842" target="_blank" rel="noopener noreferrer" className="text-[#103766] hover:underline inline-flex items-center gap-1">
            SSB 12842 <ExternalLink className="w-3 h-3" />
          </a>
        </p>
      </CardContent>
    </Card>
  );
};

export default GebyrSection;
